import React from 'react';
import CompanyHotel from '@app/model/company_hotel/CompanyHotel';
import AlertExecution from '@app/model/alert/AlertExecution';
import CompetitorsPriceRateAlertExecutionNotificationOutput
	from '@app/model/alert/output/CompetitorsPriceRateAlertExecutionNotificationOutput';
import Utilities from '@util/Utilities';
import Tag from '@app/component/tag/Tag';


const CompetitorsPriceRateUpdatedNotificationsFragment: React.FunctionComponent<{
	companyHotel: CompanyHotel,
	alertExecution: AlertExecution,
	notificationOutputs: CompetitorsPriceRateAlertExecutionNotificationOutput[],
	isInDrawer?: boolean
}> = ({ companyHotel, alertExecution, notificationOutputs, isInDrawer }) => {

	const displayAverage = (average: number) => {
		return <>
			{average > 0 && <span
				className="font-bold text-green-500">+{Utilities.formatDouble(average * 100, 0)}%</span>}
			{average < 0 && <span
				className="font-bold text-red-500">{Utilities.formatDouble(average * 100, 0)}%</span>}
		</>;
	};

	const displayAverageTag = (average: number) => {
		return <>
			{average > 0 && <Tag state="success">+{Utilities.formatDouble(average * 100, 0)}%</Tag>}
			{average < 0 && <Tag state="critical">{Utilities.formatDouble(average * 100, 0)}%</Tag>}
		</>;
	};

	const findCompetitor = (competitorId: number) => {
		return alertExecution.alertOutput.competitors!.find(c => c.id === competitorId);
	};

	return <div
		className={`${isInDrawer ? 'bg-gray-50' : 'bg-white'} mt-8 rounded-xl space-y-16 sm:space-y-20 lg:mx-0 p-4 sm:p-10`}>
		<div>
			<div className="flex items-center justify-between"><h2
				className="text-base font-semibold leading-6 text-gray-900">
				Updates Since Previous Run</h2></div>
			<div className="mt-1 flow-root">
				<div className="overflow-x-auto">
					<div className="inline-block min-w-full py-2 align-middle">
						<table className="min-w-full divide-y divide-gray-300 text-gray-500">
							<thead>
							<tr>
								<th scope="col"
										className="whitespace-nowrap py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-0">
									Date
								</th>
								<th scope="col"
										className="whitespace-nowrap py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-0">
									Room Type
								</th>
								<th scope="col"
										className="whitespace-nowrap px-2 py-3.5 text-left text-sm font-semibold text-gray-900">
									Change
								</th>
								<th scope="col"
										className="whitespace-nowrap px-2 py-3.5 text-left text-sm font-semibold text-gray-900">
									Room Found
								</th>
								<th scope="col"
										className="relative whitespace-nowrap py-3.5 pl-3 pr-4 sm:pr-0 text-left text-sm font-semibold text-gray-900">
									Percentage Since Last Run
								</th>
							</tr>
							</thead>
							<tbody className="divide-y divide-gray-200 ">
							{notificationOutputs.map(notificationOutput =>
								notificationOutput.competitorPriceRateChanges.filter(change => change.isNew || change.isUpdated).map(change => {
									return <tr key={notificationOutput.notificationId}>
										<td className="whitespace-normal px-2 py-3 text-sm">
											<Tag
												state="none">{Utilities.formatDateString(notificationOutput.statDate, 'ddd DD MMM')}</Tag>
											<br />
											{findCompetitor(change.competitorId)?.name}
										</td>
										<td className="whitespace-nowrap px-2 py-3 text-sm">
											{Utilities.snakeCaseToWords(change.current.fromRoomType)}
										</td>
										{change.isUpdated && <td className="whitespace-nowrap px-2 py-3 text-sm">
											<span>{Utilities.formatDouble(change.previous.to, 0)}</span>
											<span className="mx-1">→</span>
											<span>{Utilities.formatDouble(change.current.to, 0)}</span>
											<br />
											<span
												className="text-gray-400 italic">(Initial {Utilities.formatDouble(change.current.from, 0)})</span>
										</td>}
										{change.isNew && <td className="whitespace-nowrap px-2 py-3 text-sm">
											<span>{Utilities.formatDouble(change.current.from, 0)}</span>
											<span className="mx-1">→</span>
											<span>{Utilities.formatDouble(change.current.to, 0)}</span>
											<br />
											<span
												className="text-gray-400 italic">(First Entry)</span>
										</td>}
										{change.isUpdated && <td className="whitespace-normal px-2 py-3 text-sm">
											{change.current.fromRoomName}
											{change.current.fromRoomName === change.current.toRoomName && <>
												<br /><span
												className="text-gray-400 italic">(Same Room)</span></>}
											{change.current.fromRoomName !== change.current.toRoomName && <>
												<br /><span
												className="mx-1">→</span>{change.current.toRoomName}</>}
										</td>}
										{change.isNew && <td className="whitespace-normal px-2 py-3 text-sm">
											{change.current.fromRoomName}
											{change.current.fromRoomName === change.current.toRoomName && <>
												<br /><span
												className="text-gray-400 italic">(Same Room)</span></>}
											{change.current.fromRoomName !== change.current.toRoomName && <>
												<br /><span
												className="mx-1">→</span>{change.current.toRoomName}</>}
										</td>}
										{change.isUpdated && <td className="whitespace-nowrap px-2 py-3 text-sm">
											<span>{displayAverageTag(change.previous.rate)}</span>
											<span className="mx-1">→</span>
											<span>{displayAverageTag(change.current.rate)}</span>
										</td>}
										{change.isNew && <td className="whitespace-nowrap px-2 py-3 text-sm">
											<span>{displayAverageTag(change.current.rate)}</span>
										</td>}
									</tr>;
								}))}
							</tbody>
						</table>
					</div>
				</div>
			</div>
		</div>
	</div>;
};

export default CompetitorsPriceRateUpdatedNotificationsFragment;